"use client";

import * as React from "react";

import { InstantScanToolCircles } from "@/components/site/primitives/instant-scan-entry";
import { cn } from "@/lib/utils";

const HERO_SELECTOR = "[data-instant-scan-hero]";

/** True once the hero, with its own tool buttons, has scrolled fully above the viewport. */
function usePastHero() {
  const [pastHero, setPastHero] = React.useState(false);

  React.useEffect(() => {
    const hero = document.querySelector(HERO_SELECTOR);

    if (!hero || typeof IntersectionObserver === "undefined") {
      return;
    }

    const observer = new IntersectionObserver(([entry]) => {
      setPastHero(!entry.isIntersecting && entry.boundingClientRect.top < 0);
    });

    observer.observe(hero);

    return () => observer.disconnect();
  }, []);

  return pastHero;
}

const pillSurface =
  "border border-border/95 bg-background shadow-[0_28px_84px_-34px_rgba(15,23,42,0.32),0_14px_30px_rgba(15,23,42,0.12)] ring-1 ring-foreground/[0.05] backdrop-blur-xl";

const revealState = (visible: boolean) =>
  cn(
    "transition-[opacity,transform] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none",
    visible
      ? "pointer-events-auto translate-y-0 opacity-100"
      : "pointer-events-none translate-y-3 opacity-0",
  );

/**
 * Takes the Manual floating CTA's place while the Instant Scan entry is on, with the same
 * position, height and surface. Hidden until the visitor has scrolled past the hero.
 */
export function InstantScanFloatingCta() {
  const visible = usePastHero();

  return (
    <>
      <div
        style={{
          bottom: "max(1rem, calc(env(safe-area-inset-bottom) + 0.5rem))",
        }}
        className="pointer-events-none fixed inset-x-3 z-50 flex justify-center md:hidden"
      >
        <div
          role="group"
          aria-label="Free team scan"
          aria-hidden={!visible}
          inert={!visible}
          className={cn(
            "flex h-[68px] w-full max-w-[420px] items-center justify-between gap-2 rounded-[34px] pl-5 pr-2.5 max-[359px]:pl-4 max-[359px]:pr-2",
            pillSurface,
            revealState(visible),
          )}
        >
          <span className="flex min-w-0 flex-col gap-0.5">
            <span className="whitespace-nowrap text-sm font-semibold tracking-[-0.02em] text-foreground max-[359px]:whitespace-normal max-[359px]:leading-tight">
              Free team scan
            </span>
            <span className="whitespace-nowrap text-xs text-muted-foreground max-[359px]:hidden">
              2 minutes, read-only
            </span>
          </span>
          <InstantScanToolCircles
            placement="pill"
            routeSuffix="mobile"
            size="sm"
            className="max-[359px]:gap-0.5"
          />
        </div>
      </div>

      <div className="pointer-events-none fixed bottom-5 left-1/2 z-30 hidden -translate-x-1/2 md:block">
        <div
          role="group"
          aria-label="Free team scan"
          aria-hidden={!visible}
          inert={!visible}
          className={cn(
            "flex h-[78px] items-center gap-[22px] whitespace-nowrap rounded-[39px] pl-[30px] pr-[15px]",
            pillSurface,
            revealState(visible),
          )}
        >
          <span className="flex flex-col gap-[3px]">
            <span className="text-[15px] font-semibold tracking-[-0.02em] text-foreground">
              Find what’s slowing your team
            </span>
            <span className="text-[13px] text-muted-foreground">Free 2-minute scan</span>
          </span>
          <InstantScanToolCircles placement="pill" />
        </div>
      </div>
    </>
  );
}
